import React, { useState, useEffect, useCallback } from "react";
import { Select as ChakraSelect } from "@chakra-ui/react";
import {
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Box,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  useColorModeValue,
  useBreakpointValue,
  SimpleGrid,
  Select,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import { MdEdit, MdAdd, MdCheck, MdClose } from "react-icons/md";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import CreateCompany from "./createCompany";

export const CompanyList = () => {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [filterActive, setFilterActive] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [companiesPerPage, setCompaniesPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [editableCompany, setEditableCompany] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCreateCompanyModal, setShowCreateCompanyModal] = useState(false);
  const [services, setServices] = useState([]);
  const [users, setUsers] = useState([]);
  const [hasLogoChanged, setHasLogoChanged] = useState(false);
  const pageId = 11;
  const toast = useToast();
  const modalSize = useBreakpointValue({ base: "full", md: "3xl" });
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const [permissions, setPermissions] = useState({ canList: true, canEdit: true, canDelete: true, canInsert: true });

  useEffect(() => {
    try {
      const role = JSON.parse(sessionStorage.getItem("permissions") || "{}");
      if (!role.permissions) {
        toast({
          title: "No permissions found",
          description: "You don't have the required access.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",

        });
        return;
      }

      const permission = role.permissions.find((p) => p.page_id === pageId);
      if (permission) {
        setPermissions({
          canList: permission.read_all_access || false,
          canEdit: permission.update_access || false,
          canDelete: permission.delete_access || false,
          canInsert: permission.insert_access || false,
        });
      }
    } catch (error) {
      toast({
        title: "Error loading permissions",
        description: error.message || "Something went wrong while fetching permissions.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",

      });
    }
  }, [toast]);
  

  // Fetch companies with pagination and optional active filter (use 'active' and boolean)
  const fetchCompanies = useCallback(async (pageNum = 1) => {
    try {
      let url = `${process.env.REACT_APP_API_URL2}/companies?page=${pageNum}&limit=${companiesPerPage}&page_id=${pageId}`;
      if (filterActive !== "") {
        // API expects 'active' and a boolean value
        url += `&active=${filterActive === "true" ? 1 : filterActive === "false" ? 0 : filterActive}`;
      }
      const response = await fetchWithToken(url);
      if (!response) throw new Error("Failed to fetch companies");
      setCompanies(response.data);
      setFilteredCompanies(response.data);
      const pages = response.pagination?.total_pages || response.pagination?.last_page || 1;
      setTotalPages(pages);
      toast({
        title: "Companies fetched successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      toast({
        title: "Error fetching companies.",
        description: error.message || "",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  }, [companiesPerPage, filterActive, toast]);

  useEffect(() => {
    fetchCompanies(currentPage);
  }, [fetchCompanies, currentPage, companiesPerPage, filterActive]);

  // Fetch available services for the select input
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetchWithToken(
          `${process.env.REACT_APP_API_URL}/services?page_id=${pageId}` // updated API URL
        );
        if (!response) throw new Error("Failed to fetch services");
        setServices(response.data);
      } catch (error) {
        toast({
          title: "Error fetching services.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      }
    };

    fetchServices();
  }, [toast]);

  // Fetch available users for the select input
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetchWithToken(
          `${process.env.REACT_APP_API_URL}/users?page_id=${pageId}`
        );
        if (!response) throw new Error("Failed to fetch users");
        if (response) {
          // Filter users with role.id === 3
          const filteredUsers = response.data.filter(user => user.role?.id === 3);
          setUsers(filteredUsers);
        }
      } catch (error) {
        toast({
          title: "Error fetching users.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",

        });
      }
    };

    fetchUsers();
  }, [toast]);

  const handleEditCompany = (company) => {
    setEditableCompany(company);
    setHasLogoChanged(false); // Reset logo change flag when starting to edit
  };

  const handleCloseEditModal = () => {
    setEditableCompany(null);
    setHasLogoChanged(false); // Reset logo change flag when closing modal
  };
  // Convert an uploaded file to a base64 string for the logo field.
 const handleLogoChange = (e) => {
  const file = e.target.files[0];
  if (file) {
    setEditableCompany({
      ...editableCompany,
      logo: file,
    });
    setHasLogoChanged(true);
  }
};

const handleProfileImage1Change = (e) => {
  const file = e.target.files[0];
  if (file) {
    setEditableCompany({
      ...editableCompany,
      profile_image_1: file,
    });
  }
};

const handleProfileImage2Change = (e) => {
  const file = e.target.files[0];
  if (file) {
    setEditableCompany({
      ...editableCompany,
      profile_image_2: file,
    });
  }
};

const handleProfileImage3Change = (e) => {
  const file = e.target.files[0];
  if (file) {
    setEditableCompany({
      ...editableCompany,
      profile_image_3: file,
    });
  }
};
const handleSaveCompany = async () => {
  try {
    const formData = new FormData();
    formData.append("company_id", editableCompany.id);
    formData.append("name", editableCompany.name);
    formData.append("service_id", editableCompany.service_id);
    formData.append("user_id", editableCompany.user_id);

    formData.append("uclean_commission", editableCompany.uclean_commission);
    formData.append("item_commission", editableCompany.item_commission);

    // Send is_active as "1" or "0" string
    formData.append("is_active", editableCompany.is_active ? "1" : "0");
    formData.append("page_id", pageId);

    // Only include logo if it has been changed and is a File object
    if (hasLogoChanged && editableCompany.logo && typeof editableCompany.logo !== "string") {
      formData.append("logo", editableCompany.logo);
    }

    // Only append profile images if they are File objects (not URLs)
    if (editableCompany.profile_image_1 && typeof editableCompany.profile_image_1 !== "string") {
      formData.append("profile_images[profile_image_1]", editableCompany.profile_image_1);
    }
    if (editableCompany.profile_image_2 && typeof editableCompany.profile_image_2 !== "string") {
      formData.append("profile_images[profile_image_2]", editableCompany.profile_image_2);
    }
    if (editableCompany.profile_image_3 && typeof editableCompany.profile_image_3 !== "string") {
      formData.append("profile_images[profile_image_3]", editableCompany.profile_image_3);
    }

    // Add _method for PATCH override
    formData.append("_method", "PATCH");

    setIsSubmitting(true);
    const response = await fetchWithToken(
      `${process.env.REACT_APP_API_URL2}/company/update?page_id=${pageId}`,
      "POST",
      formData
    );
    if (!response) throw new Error("Failed to update company");
    setCompanies((prev) =>
      prev.map((c) => (c.id === editableCompany.id ? editableCompany : c))
    );
    setFilteredCompanies((prev) =>
      prev.map((c) => (c.id === editableCompany.id ? editableCompany : c))
    );
    handleCloseEditModal();
    toast({
      title: "Company updated successfully.",
      status: "success",
      duration: 5000,
      isClosable: true,
      position: "top-right",
    });
  } catch (error) {
    toast({
      title: "Error updating company.",
      description: error.message || "",
      status: "error",
      duration: 5000,
      isClosable: true,
      position: "top-right",
    });
  } finally {
    setIsSubmitting(false);
  }
};

  const handleUpdateStatus = async (company, newStatus) => {
    try {
      const data = {
        company_id: company.id,
        is_active: newStatus,
        page_id: pageId,
      };
      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_URL2}/company/status/update`,
        "PATCH",
        data
      );
      if (!response) throw new Error("Failed to update company status");
      // Update status in both state arrays
      setCompanies((prev) =>
        prev.map((c) =>
          c.id === company.id ? { ...c, is_active: newStatus } : c
        )
      );
      setFilteredCompanies((prev) =>
        prev.map((c) =>
          c.id === company.id ? { ...c, is_active: newStatus } : c
        )
      );
      toast({
        title: newStatus
          ? "Company accepted successfully."
          : "Company rejected successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
position: "top-right",

      });
    } catch (error) {
      toast({
        title: "Error updating company status.",
        status: "error",
        duration: 5000,
        isClosable: true,
position: "top-right",

      });
    }
  };

  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    setFilteredCompanies(
      companies.filter((company) =>
        company.name.toLowerCase().includes(query.toLowerCase())
      )
    );
    setCurrentPage(1);
  };
const getImagePreview = (file) => {
  if (!file) return null;
  if (typeof file === "string") return file;
  return URL.createObjectURL(file);
};
  return (
    <Card
      direction="column"
      w="100%"
      px="0px"
      overflowX={{ sm: "scroll", lg: "hidden" }}
    >
      <Flex
        px={{ base: "10px", md: "25px" }}
        justify="space-between"
        mb="20px"
        align="center"
        flexWrap="wrap"
      >
        <Flex align="center">
          <Text
            color="gray.800"
            fontSize={{ base: "18px", md: "22px" }}
            fontWeight="700"
            lineHeight="100%"
            mb={{ base: "10px", md: "0" }}
          >
            {t("Service Provider List")}
          </Text>
          <Button
            onClick={() => setShowCreateCompanyModal(true)}
            mb={1}
            ml={4}
            isDisabled={!permissions.canInsert}
            colorScheme="cyan"
            color="white"
            borderRadius="full"
            boxShadow="sm"
            _hover={{ bg: "cyan.600", transform: "scale(1.05)" }}
            _active={{ bg: "cyan.700", transform: "scale(0.95)" }}
            transition="all 0.2s"
          >
            <MdAdd />
          </Button>
        </Flex>
          <FormControl maxW={{ base: "100%", md: "120px" }} ml="auto" mr={4}>
            <Select
              value={companiesPerPage}
              onChange={(e) => {
                setCompaniesPerPage(parseInt(e.target.value, 10));
                setCurrentPage(1);
              }}
              size="sm"
              focusBorderColor="blue.400"
              borderRadius={"md"}
              _hover={{ borderColor: "blue.400",
                boxShadow: "0 0 0 1px #3182CE",
                color: "blue.400",
                backgroundColor: "blue.50"
              }}
            >
              <option value="10">10 {t("per page")}</option>
              <option value="20">20 {t("per page")}</option>
              <option value="50">50 {t("per page")}</option>
            </Select>
          </FormControl>
          {/* Active Status Filter */}
          <FormControl maxW={{ base: "100%", md: "180px" }} mr={4}>
            <ChakraSelect
              placeholder={t("Select Active Status")}
              value={filterActive}
              onChange={(e) => {
                setFilterActive(e.target.value);
                setCurrentPage(1);
              }}
              size="sm"
              focusBorderColor="blue.400"
              borderRadius={"md"}
              _hover={{ borderColor: "blue.400",
                boxShadow: "0 0 0 1px #3182CE",
                color: "blue.400",
                backgroundColor: "blue.50"
              }}
            >
              <option value="true">{t("Active: Yes")}</option>
              <option value="false">{t("Active: No")}</option>
            </ChakraSelect>
          </FormControl>
        <Input
          placeholder={t("Search company...")}
          value={searchQuery}
          onChange={handleSearch}
          size="sm"
          w={{ base: "100%", md: "200px" }}
        />
      </Flex>
       <Box overflowY="auto" maxHeight="400px">
            <Table variant="simple" color="gray.500" mb="24px">
              <Thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                  <Tr bg='rgb(69, 50, 240)' textColor="white" style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <Th textColor="white">{t("Index")}</Th>
              <Th textColor="white">{t("Name")}</Th>
              <Th textColor="white">{t("Service")}</Th>
              <Th textColor="white">{t("Manager")}</Th>
              <Th textColor="white">{t("Commission")}%</Th>
              <Th textColor="white">{t("Item Commission")} %</Th>
              <Th textColor="white">{t("Active")}</Th>
              <Th textColor="white">{t("Actions")}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredCompanies.map((company, index) => (
              <Tr key={company.id}>
                <Td>{index + 1}</Td>
                <Td>{company.name}</Td>
                <Td>{services.find(service => service.id === company.service_id)?.name || company.service_id}</Td> {/* updated to show service name */}
                <Td>{company.manager_name}</Td>
                <Td>{company.uclean_commission}</Td>
                <Td>{company.item_commission}</Td>
                <Td>{company.is_active ? "Yes" : "No"}</Td>
                <Td>
                  <Flex>
                    <Button
                      onClick={() => handleEditCompany(company)}
                      colorScheme="cyan"
                      size="sm"
                      isDisabled={!permissions.canEdit}
                      borderRadius="full"
                      boxShadow="md"
                      color="white"
                      _hover={{ bg: "cyan.600", transform: "scale(1.05)" }}
                      _active={{ bg: "cyan.700", transform: "scale(1.05)" }}
                      transition="all 0.2s"
                      leftIcon={<MdEdit />}
                    >
                      {t("Edit")}
                    </Button>
                    {/* <Button
                      // Uncomment below to enable deletion:
                      // onClick={() => handleDeleteCompany(company)}
                      colorScheme="red"
                      size="sm"
                      ml={2}
                      borderRadius="full"
                      boxShadow="md"
                      _hover={{ bg: "red.600", transform: "scale(1.05)" }}
                      _active={{ bg: "red.700", transform: "scale(0.95)" }}
                      transition="all 0.2s"
                    >
                      <MdDelete />
                    </Button> */}
                    <Button
                      onClick={() => handleUpdateStatus(company, true)}
                      colorScheme="green"
                      size="sm"
                      ml={2}
                      isDisabled={!permissions.canEdit || company.is_active === true }
                      borderRadius="full"
                      boxShadow="md"
                      _hover={{ bg: "green.600", transform: "scale(1.05)" }}
                      _active={{ bg: "green.700", transform: "scale(0.95)" }}
                      transition="all 0.2s"
                      leftIcon={<MdCheck />}
                    >
                      {t("Accept")}
                    </Button>
                    <Button
                      onClick={() => handleUpdateStatus(company, false)}
                      colorScheme="orange"
                      size="sm"
                      ml={2}
                      isDisabled={!permissions.canEdit || company.is_active === false }
                      borderRadius="full"
                      boxShadow="md"
                      _hover={{ bg: "orange.600", transform: "scale(1.05)" }}
                      _active={{ bg: "orange.700", transform: "scale(0.95)" }}
                      transition="all 0.2s"
                      leftIcon={<MdClose />}
                    >
                      {t("Reject")}
                    </Button>
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      {/* Pagination Controls */}
      {totalPages > 0 && (
        <Box mt={4} mb={4}>
          <Text textAlign="center" fontSize="sm" color="gray.600" mb={3}>
            {t("Page")} {currentPage} {t("of")} {totalPages}
            {filteredCompanies.length > 0 && (
              <span> - {t("Showing")} {((currentPage - 1) * companiesPerPage) + 1} {t("to")} {Math.min(currentPage * companiesPerPage, ((currentPage - 1) * companiesPerPage) + filteredCompanies.length)} {t("results")}</span>
            )}
          </Text>
          <Flex justifyContent="center" wrap="wrap" gap={2}>
            <Button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              size="sm"
              variant="outline"
              display={{ base: currentPage > 3 ? "block" : "none", md: currentPage > 2 ? "block" : "none" }}
            >
              {t("First")}
            </Button>
            <Button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              size="sm"
              colorScheme="cyan"
              variant="outline"
            >
              {t("Previous")}
            </Button>
            {/* Page Numbers with smart ellipsis */}
            {(() => {
              const delta = 2;
              const range = [];
              const rangeWithDots = [];
              for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
                range.push(i);
              }
              if (currentPage - delta > 2) {
                rangeWithDots.push(1, '...');
              } else {
                rangeWithDots.push(1);
              }
              rangeWithDots.push(...range);
              if (currentPage + delta < totalPages - 1) {
                rangeWithDots.push('...', totalPages);
              } else if (totalPages > 1) {
                rangeWithDots.push(totalPages);
              }
              return rangeWithDots.map((page, index) => {
                if (page === '...') {
                  return (
                    <Text key={`ellipsis-${index}`} mx={2} alignSelf="center" fontSize="sm" color="gray.500">...</Text>
                  );
                }
                return (
                  <Button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    variant={currentPage === page ? "solid" : "outline"}
                    colorScheme={currentPage === page ? "cyan" : "gray"}
                    color={'blue'}
                    size="sm"
                    minW="40px"
                  >
                    {page}
                  </Button>
                );
              });
            })()}
            <Button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              size="sm"
              colorScheme="cyan"
              variant="outline"
            >
              {t("Next")}
            </Button>
            <Button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              size="sm"
              variant="outline"
              display={{ base: currentPage < totalPages - 2 ? "block" : "none", md: currentPage < totalPages - 1 ? "block" : "none" }}
            >
              {t("Last")}
            </Button>
          </Flex>
        </Box>
      )}
      {editableCompany && (
        <Modal
          isOpen={true}
          onClose={handleCloseEditModal}
          size={modalSize}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <Text color={"blue.500"} fontSize="2xl" fontWeight="700">
                {t("Edit Service Provider")}
              </Text>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <SimpleGrid columns={2} spacing={4}>
                {/* Name */}
                <FormControl isRequired>
                  <FormLabel color="blue.600">{t("Name")}</FormLabel>
                  <Input
                    value={editableCompany.name}
                    onChange={(e) =>
                      setEditableCompany({
                        ...editableCompany,
                        name: e.target.value,
                      })
                    }
                  />
                </FormControl>
                {/* Service Select */}
                <FormControl isRequired>
                  <FormLabel color="blue.600">{t("Service")}</FormLabel>
                  <Select
                    value={editableCompany.service_id}
                    onChange={(e) =>
                      setEditableCompany({
                        ...editableCompany,
                        service_id: parseInt(e.target.value),
                      })
                    }
                  >
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                {/* User Select */}
                <FormControl isRequired>
                  <FormLabel color="blue.600">{t("User")}</FormLabel>
                  <Select
                    value={editableCompany.manager_id}
                    onChange={(e) =>
                      setEditableCompany({
                        ...editableCompany,
                        user_id: parseInt(e.target.value),
                      })
                    }
                    placeholder={t("Select a user")}
                  >
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                {/* Commission */}
                <FormControl isRequired>
                  <FormLabel color="blue.600">{t("Uclean Commission %")}</FormLabel>
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    value={editableCompany.uclean_commission}
                    onChange={(e) =>
                      setEditableCompany({
                        ...editableCompany,
                        uclean_commission: parseFloat(e.target.value),
                      })
                    }
                  />
                </FormControl>
                {/* Item Commission */}
                <FormControl isRequired>
                  <FormLabel color="blue.600">{t("Item Commission %")}</FormLabel>
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    value={editableCompany.item_commission}
                    onChange={(e) =>
                      setEditableCompany({
                        ...editableCompany,
                        item_commission: parseFloat(e.target.value),
                      })
                    }
                  />
                </FormControl>
                {/* Active Select */}
                <FormControl isRequired>
                  <FormLabel color="blue.600">{t("Active")}</FormLabel>
                  <Select
                    value={editableCompany.is_active}
                    onChange={(e) =>
                      setEditableCompany({
                        ...editableCompany,
                        is_active: JSON.parse(e.target.value),
                      })
                    }
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </Select>
                </FormControl>
                {/* Logo Upload */}
                <FormControl>
                  <FormLabel color="blue.600">{t("Logo")}</FormLabel>
                  <Input type="file" accept="image/*" onChange={handleLogoChange} />
                  {/* Add file size and preview logic here if needed */}
                </FormControl>
                {/* Profile Image 1 */}
                <FormControl>
                  <FormLabel color="blue.600">{t("Profile Image 1")}</FormLabel>
                  <Input type="file" accept="image/*" onChange={handleProfileImage1Change} />
                  {/* Add file size and preview logic here if needed */}
                </FormControl>
                {/* Profile Image 2 */}
                <FormControl>
                  <FormLabel color="blue.600">{t("Profile Image 2")}</FormLabel>
                  <Input type="file" accept="image/*" onChange={handleProfileImage2Change} />
                  {/* Add file size and preview logic here if needed */}
                </FormControl>
                {/* Profile Image 3 */}
                <FormControl>
                  <FormLabel color="blue.600">{t("Profile Image 3")}</FormLabel>
                  <Input type="file" accept="image/*" onChange={handleProfileImage3Change} />
                  {/* Add file size and preview logic here if needed */}
                </FormControl>
              </SimpleGrid>
              {/* Show all selected images with labels at the bottom */}
              <Box mt={8}>
                <Text fontWeight="bold" mb={2}>{t("Selected Images")}</Text>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                  {editableCompany.logo && (
                    <Box textAlign="center">
                      <Text fontSize="sm" mb={1}>{t("Logo")}</Text>
                      <img src={getImagePreview(editableCompany.logo)} alt="Logo Preview" style={{ maxWidth: "100%", maxHeight: "120px", borderRadius: "8px" }} />
                    </Box>
                  )}
                  {editableCompany.profile_image_1 && (
                    <Box textAlign="center">
                      <Text fontSize="sm" mb={1}>{t("Profile Image 1")}</Text>
                      <img src={getImagePreview(editableCompany.profile_image_1)} alt="Profile 1 Preview" style={{ maxWidth: "100%", maxHeight: "120px", borderRadius: "8px" }} />
                    </Box>
                  )}
                  {editableCompany.profile_image_2 && (
                    <Box textAlign="center">
                      <Text fontSize="sm" mb={1}>{t("Profile Image 2")}</Text>
                      <img src={getImagePreview(editableCompany.profile_image_2)} alt="Profile 2 Preview" style={{ maxWidth: "100%", maxHeight: "120px", borderRadius: "8px" }} />
                    </Box>
                  )}
                  {editableCompany.profile_image_3 && (
                    <Box textAlign="center">
                      <Text fontSize="sm" mb={1}>{t("Profile Image 3")}</Text>
                      <img src={getImagePreview(editableCompany.profile_image_3)} alt="Profile 3 Preview" style={{ maxWidth: "100%", maxHeight: "120px", borderRadius: "8px" }} />
                    </Box>
                  )}
                </SimpleGrid>
              </Box>
            </ModalBody>
            <ModalFooter>
              <Button
                onClick={handleSaveCompany}
                isLoading={isSubmitting}
                color="white"
                colorScheme="cyan"
              >
                Save
              </Button>
              <Button
                onClick={handleCloseEditModal}
                colorScheme="red"
                ml={3}
              >
                Cancel
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
      <Modal
        isOpen={showCreateCompanyModal}
        onClose={() => setShowCreateCompanyModal(false)}
        size={modalSize}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text color={"blue.500"} fontSize="2xl" fontWeight="700">
              {t("Create Service Provider")}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <CreateCompany />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default CompanyList;
